import {
  Activity,
  ArrowRight,
  BatteryMedium,
  BookOpenCheck,
  BriefcaseBusiness,
  CalendarClock,
  CheckCircle2,
  CircleDashed,
  Coins,
  Compass,
  Dumbbell,
  HeartHandshake,
  ListChecks,
  Plus,
  Settings2,
  Sparkles,
  Target
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { formatDateTime, relativeDue } from "../lib/format";
import { ProjectCard, TaskRow } from "../components/records";
import { Button, Card, EmptyState, Metric, PageHeader, SectionHeading } from "../components/ui";
import { CompoundPanel } from "../components/CompoundPanel";
import { useMemo, useEffect, useState } from "react";
import { api } from "../lib/api";
import type { LearningPlan } from "../types";

// 订单状态 → 可拍素材建议（生产流程实拍）
const SHOOT_MAP: Array<[string, string]> = [
  ["SAMPLING", "打样工序（裁片/样衣）"],
  ["PRODUCTION", "车间生产（车缝流水线）"],
  ["QUALITY_CHECK", "验货细节（质检）"],
  ["SHIPPING", "装箱出货"]
];

function localDateKey(value: Date, timezone = "Asia/Shanghai") {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat("en-CA", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(value);
  } catch {
    parts = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Shanghai", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(value);
  }
  return ["year", "month", "day"].map((type) => parts.find((part) => part.type === type)?.value).join("-");
}
const dimensionIcons = {
  "attr-career": BriefcaseBusiness,
  "attr-wealth": Coins,
  "attr-experience": Compass,
  "attr-physique": Dumbbell
} as const;

export function TodayPage() {
  const { data, dataRevision, openModal } = useApp();
  const navigate = useNavigate();
  if (!data) return null;

  const openTasks = data.tasks.filter(
    (task) => !["COMPLETED", "CANCELLED", "ARCHIVED"].includes(task.status)
  );
  const topTasks = [...openTasks].sort((left, right) => right.recommendation_score - left.recommendation_score);
  const now = new Date();
  const timezone = data.profile?.timezone || "Asia/Shanghai";
  const todayKey = localDateKey(now, timezone);
  const monthKey = todayKey.slice(0, 7);
  const [year, month] = todayKey.split("-").map(Number);
  const monthEndKey = `${monthKey}-${new Date(Date.UTC(year!, month!, 0)).getUTCDate()}`;
  const todayTasks = topTasks
    .filter((task) => task.pinned || (task.due_date && task.due_date.slice(0, 10) <= todayKey));
  const todayTaskIds = new Set(todayTasks.map((task) => task.id));
  const monthlyTasks = topTasks
    .filter((task) => task.due_date?.slice(0, 7) === monthKey && !todayTaskIds.has(task.id))
    .sort((left, right) => {
      const dateOrder = (left.due_date ?? monthEndKey).localeCompare(right.due_date ?? monthEndKey);
      return dateOrder || right.recommendation_score - left.recommendation_score;
    });
  const monthlyOverdue = monthlyTasks.filter((task) => (task.due_date?.slice(0, 10) ?? todayKey) < todayKey).length;
  const monthlyCompleted = data.tasks.filter(
    (task) => task.status === "COMPLETED" && task.due_date?.slice(0, 7) === monthKey
  ).length;
  const todayCompleted = data.tasks.filter(
    (task) => task.status === "COMPLETED" && task.completed_at && localDateKey(new Date(task.completed_at), timezone) === todayKey
  ).length;
  const activeProjects = data.projects.filter((project) => project.status === "ACTIVE");
  const weekAgo = Date.now() - 7 * 86_400_000;
  const recentEvents = data.events.filter((event) => ["RESULT", "OUTCOME", "LEARNING", "APPLICATION", "MILESTONE"].includes(event.event_type) && new Date(event.occurred_at).getTime() >= weekAgo);
  const evidenceCount = data.skills.reduce((total, skill) => total + Number(skill.evidence_count), 0);
  const hour = new Date().getHours();
  const greeting = hour < 11 ? "早上好" : hour < 18 ? "下午好" : "晚上好";
  const displayName = data.profile?.display_name;

  // 订单跟进提醒：今天到期或已逾期的未完成订单
  const followups = useMemo(() => {
    const today = todayKey;
    return data.customerOrders
      .filter(
        (order) =>
          order.next_followup &&
          !["COMPLETED", "CANCELLED", "PAUSED"].includes(order.status)
      )
      .map((order) => {
        const due = relativeDue(order.next_followup);
        const isOverdue = Boolean(due?.includes("逾期"));
        const isToday = order.next_followup!.slice(0, 10) === today;
        return { order, due, isOverdue, isToday };
      })
      .filter((item) => item.isOverdue || item.isToday)
      .sort((a, b) => Number(b.isOverdue) - Number(a.isOverdue) || (a.order.next_followup ?? "").localeCompare(b.order.next_followup ?? ""))
      .slice(0, 5);
  }, [data.customerOrders, todayKey]);

  const [learningPlans, setLearningPlans] = useState<LearningPlan[]>([]);
  useEffect(() => {
    let active = true;
    void api.listLearningPlans().then((plans) => { if (active) setLearningPlans(plans); }).catch(() => { /* Keep the last successful snapshot. */ });
    return () => { active = false; };
  }, [dataRevision]);

  const studyTasks = learningPlans
    .filter((plan) => plan.currentDay)
    .slice(0, 3);
  const shootIdeas = data.customerOrders
    .map((order) => {
      const match = SHOOT_MAP.find(([status]) => status === order.status);
      return match ? { order, idea: match[1] } : null;
    })
    .filter((item) => item !== null)
    .slice(0, 3);

  return (
    <div className="page page--today">
      <PageHeader
        eyebrow="TODAY · 个人驾驶舱"
        title={`${greeting}${displayName ? `，${displayName}` : ""}`}
        description="持续行动，长期主义，复利人生。"
        actions={
          <Button variant="secondary" icon={<Settings2 size={16} />} onClick={() => openModal("profile")}>
            {data.profile ? "状态打卡" : "完成设置"}
          </Button>
        }
      />

      {!data.profile && (
        <Card className="welcome-card">
          <div className="welcome-card__orb"><Sparkles size={25} /></div>
          <div>
            <span className="eyebrow">你的世界还很安静</span>
            <h2>从现在开始，只记录真实发生的成长</h2>
            <p>这里不会预填销售额、经验值或能力分。先设置称呼与当前状态，再创建第一个真正重要的目标。</p>
          </div>
          <div className="welcome-card__actions">
            <Button onClick={() => openModal("profile")} icon={<ArrowRight size={16} />}>开始设置</Button>
            <Button variant="ghost" onClick={() => openModal("goal")}>直接创建目标</Button>
          </div>
        </Card>
      )}

      <div className="planning-grid" aria-label="月度与今日行动计划">
        <Card className="planning-card planning-card--month">
          <SectionHeading
            title="月度计划"
            description={month + " 月承诺 · 完成后沉淀为项目与技能的真实证据。"}
            action={
              <Button
                variant="ghost"
                size="sm"
                onClick={() => openModal("task", { dueDate: monthEndKey })}
                icon={<Plus size={14} />}
              >
                添加本月任务
              </Button>
            }
          />
          <div className="planning-summary" aria-label="本月任务概览">
            <span><b>{monthlyTasks.length}</b> 待推进</span>
            <span className={monthlyOverdue ? "is-alert" : ""}><b>{monthlyOverdue}</b> 已逾期</span>
            <span><b>{monthlyCompleted}</b> 已完成</span>
          </div>
          {monthlyTasks.length ? (
            <div className="task-list planning-task-list">
              {monthlyTasks.map((task) => <TaskRow key={task.id} task={task} showReason />)}
            </div>
          ) : (
            <EmptyState
              icon={<CalendarClock size={21} />}
              title="本月还没有排定任务"
              description="先安排截止日期；完成后才进入时间轴，关联技能才积累相应经验。"
              actionLabel="安排本月第一项"
              onAction={() => openModal("task", { dueDate: monthEndKey })}
              compact
            />
          )}
        </Card>

        <Card className="planning-card planning-card--today">
          <SectionHeading
            title="今日计划"
            description="今天到期、已逾期和主动置顶的事项，都在这里。"
            action={
              <Button
                variant="ghost"
                size="sm"
                onClick={() => openModal("task", { dueDate: todayKey, pinned: true })}
                icon={<Plus size={14} />}
              >
                添加今日任务
              </Button>
            }
          />
          <div className="planning-summary planning-summary--today" aria-label="今日任务概览">
            <span><b>{todayTasks.length}</b> 待推进</span>
            <span><b>{todayCompleted}</b> 今日完成</span>
          </div>
          {todayTasks.length ? (
            <div className="task-list planning-task-list">
              {todayTasks.map((task) => <TaskRow key={task.id} task={task} showReason />)}
            </div>
          ) : (
            <EmptyState
              icon={<Target size={21} />}
              title="今天没有必须推进的事项"
              description="你可以从月度计划中选择一项置顶，或创建今天的第一步。"
              actionLabel="创建今日任务"
              onAction={() => openModal("task", { dueDate: todayKey, pinned: true })}
              compact
            />
          )}
        </Card>

        <Card className="status-card planning-status-card">
          <SectionHeading title="此刻状态" description="承认此刻，才能更准确地出发。" />
          <div className="state-checkin">
            <StateItem label="精力" value={data.profile?.energy} icon={<BatteryMedium size={18} />} />
            <StateItem label="专注" value={data.profile?.focus} icon={<CircleDashed size={18} />} />
            <StateItem label="情绪" value={data.profile?.mood} icon={<HeartHandshake size={18} />} />
          </div>
          <button className="inline-action" onClick={() => openModal("profile")}>
            {data.profile?.energy ? "更新今日状态" : "完成一次 10 秒打卡"}
            <ArrowRight size={15} />
          </button>
        </Card>
        <Card className="compound-card today-compound-card"><CompoundPanel /></Card>
      </div>

      <div className="metrics-grid">
        <Metric
          label="活跃项目"
          value={activeProjects.length}
          meta={activeProjects.length ? "来自真实项目" : "从第一个项目开始"}
          icon={<Target size={19} />}
          onClick={() => navigate("/projects")}
        />
        <Metric
          label="待推进事项"
          value={openTasks.length}
          meta={topTasks.length ? "已按价值与紧迫度排序" : "当前没有积压"}
          icon={<ListChecks size={19} />}
          onClick={() => navigate("/projects?tab=tasks")}
        />
        <Metric
          label="近 7 天成果"
          value={data.eventStats?.recentOutcomes ?? recentEvents.length}
          meta={(data.eventStats?.recentOutcomes ?? recentEvents.length) ? "完成与应用事实，不含状态变更" : "尚未记录成果"}
          icon={<Activity size={19} />}
          onClick={() => navigate("/timeline")}
        />
        <Metric
          label="技能证据"
          value={evidenceCount}
          meta={evidenceCount ? "由事件流水聚合" : "不会默认赠送经验"}
          icon={<BookOpenCheck size={19} />}
          onClick={() => navigate("/learning")}
        />
      </div>

      <div className="today-focus">
        <Card className="today-focus__card">
          <header className="today-focus__head">
            <span className="today-focus__num">01</span>
            <div>
              <span className="eyebrow">FOLLOW UP · 跟进</span>
              <h3>业务推进与客户跟进</h3>
            </div>
          </header>
          {(data.workbench?.topItems.length ?? 0) > 0 && (
            <div className="today-focus__list">
              {data.workbench!.topItems.slice(0, 3).map((item) => (
                <button key={item.id} className="today-focus__item" onClick={() => navigate("/work/desk")}>
                  <strong>{item.title}</strong>
                  <small>{item.status === "waiting" ? "等待外部" : item.status === "doing" ? "进行中" : "待处理"}{item.nextAction ? ` · ${item.nextAction}` : ""}</small>
                </button>
              ))}
            </div>
          )}
          {followups.length ? (
            <div className="today-focus__list">
              {followups.slice(0, 3).map(({ order, due, isOverdue }) => (
                <button key={order.id} className={`today-focus__item ${isOverdue ? "overdue" : ""}`} onClick={() => navigate("/work")}>
                  <strong>{order.contact_name} · {order.title}</strong>
                  <small>{due ?? order.next_followup}</small>
                </button>
              ))}
            </div>
          ) : !data.workbench?.open ? (
            <p className="today-focus__empty">今天没有到期的跟进，可以去开发新客户。</p>
          ) : <p className="today-focus__empty">业务待推进 {data.workbench.open} 项 · 已逾期 {data.workbench.overdue} 项</p>}
          <button className="today-focus__more" onClick={() => navigate("/work/desk")}>去业务工作台 <ArrowRight size={13} /></button>
        </Card>

        <Card className="today-focus__card">
          <header className="today-focus__head">
            <span className="today-focus__num">02</span>
            <div>
              <span className="eyebrow">STUDY · 学习</span>
              <h3>今日学习任务</h3>
            </div>
          </header>
          {studyTasks.length ? (
            <div className="today-focus__list">
              {studyTasks.map((plan) => (
                <button key={plan.id} className="today-focus__item" onClick={() => navigate("/plans")}>
                  <strong>{plan.currentDay?.title}</strong>
                  <small>{plan.title} · Day {plan.currentDay?.day_no}/{plan.total_days}</small>
                </button>
              ))}
            </div>
          ) : (
            <p className="today-focus__empty">还没有学习计划，去建一个 21 天计划吧。</p>
          )}
          <button className="today-focus__more" onClick={() => navigate("/plans")}>去学习计划 <ArrowRight size={13} /></button>
        </Card>

        <Card className="today-focus__card">
          <header className="today-focus__head">
            <span className="today-focus__num">03</span>
            <div>
              <span className="eyebrow">SHOOT · 素材</span>
              <h3>今天可拍的素材</h3>
            </div>
          </header>
          {shootIdeas.length ? (
            <div className="today-focus__list">
              {shootIdeas.map(({ order, idea }) => (
                <button key={order.id} className="today-focus__item" onClick={() => navigate("/work")}>
                  <strong>{idea}</strong>
                  <small>{order.contact_name} · {order.title}</small>
                </button>
              ))}
            </div>
          ) : (
            <p className="today-focus__empty">订单到生产阶段后，这里会自动提示拍什么。</p>
          )}
          <button className="today-focus__more" onClick={() => navigate("/work")}>去工作台 <ArrowRight size={13} /></button>
        </Card>
      </div>

      <div className="dashboard-grid dashboard-grid--wide today-secondary-grid">
        <Card>
          <SectionHeading
            title="核心目标与项目"
            description="方向一旦清晰，行动就有了力量。"
            action={<Link className="text-link" to="/projects">查看全部 <ArrowRight size={14} /></Link>}
          />
          {activeProjects.length ? (
            <div className="project-preview-grid">
              {activeProjects.slice(0, 2).map((project) => <ProjectCard project={project} key={project.id} />)}
            </div>
          ) : (
            <EmptyState
              icon={<Target size={21} />}
              title="尚未建立推进主线"
              description="先创建一个目标，再用项目把它变成可交付结果。"
              actionLabel="创建项目"
              onAction={() => openModal("project")}
              compact
            />
          )}
        </Card>

        <Card className="attributes-card">
          <SectionHeading
            title="长期成长"
            description="真正的蜕变，都藏在无人喝彩的坚持里。"
            action={<Link className="text-link" to="/growth">查看阶段 <ArrowRight size={14} /></Link>}
          />
          <div className="attribute-list">
            {data.dimensions.map((dimension) => {
              const Icon = dimensionIcons[dimension.id];
              return (
                <div className="attribute-row" key={dimension.id}>
                  <span className="attribute-row__icon"><Icon size={16} /></span>
                  <span>{dimension.title}</span>
                  <div className="attribute-row__track">
                    <i style={{ width: `${dimension.stage_progress}%` }} />
                  </div>
                  <small>{dimension.stage_name} · {dimension.grade} 级</small>
                </div>
              );
            })}
          </div>
          <div className="data-note">
            <CircleDashed size={16} />
            日常记录只给少量经验；财富只读取最新人民币资产，财务计划不计入。
          </div>
        </Card>
      </div>

      <Card className="capture-card today-capture-card">
        <div className="capture-card__icon"><Sparkles size={21} /></div>
        <div>
          <span className="eyebrow">QUICK CAPTURE</span>
          <h2>接下来要做什么？</h2>
          <p>先加入事项；完成后系统会自动把结果写入时间线，并联动技能与长期成长。</p>
        </div>
        <Button onClick={() => openModal("quick")} icon={<Plus size={16} />}>新增事项</Button>
        <small>{data.meta.syncedAt ? `上次同步 ${formatDateTime(data.meta.syncedAt)}` : ""}</small>
      </Card>
    </div>
  );
}

function StateItem({
  label,
  value,
  icon
}: {
  label: string;
  value: number | null | undefined;
  icon: React.ReactNode;
}) {
  return (
    <div className="state-item">
      <span>{icon}</span>
      <div>
        <small>{label}</small>
        <strong>{value ? `${value} / 5` : "未记录"}</strong>
      </div>
      <div className="state-dots">
        {[1, 2, 3, 4, 5].map((point) => <i key={point} className={value && point <= value ? "filled" : ""} />)}
      </div>
    </div>
  );
}
